
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  HelpCircle,
  MessageSquare,
  Mail,
  CheckCircle2,
  BookOpen,
  Search
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

const DEFAULT_FAQS = [
  {
    question: "When will Knight Arena Alpha be released?",
    answer: "We're targeting Q2 2026 for alpha testing, with full release estimated for late 2027. We're committed to releasing when the game is ready, not before."
  },
  {
    question: "Will Knight Arena be free to play?",
    answer: "Yes! Knight Arena is 100% free to play. Our store only contains cosmetic items and convenience features - never pay-to-win items."
  },
  {
    question: "What platforms will Knight Arena be available on?",
    answer: "Initially PC only. We're focusing on creating the best possible experience before considering other platforms."
  },
  {
    question: "How can I join the alpha testing?",
    answer: "Register for an account and we'll notify you when alpha testing begins. Priority will be given to early supporters and active community members."
  },
  {
    question: "Is there a way to support development?",
    answer: "We appreciate the thought, but we're self-funded and not accepting donations or investments. Just spreading the word and providing feedback helps us immensely."
  },
  {
    question: "What makes Knight Arena different from other MMOs?",
    answer: "We're built by gamers who refuse to compromise. No shareholders, no pay-to-win, intense PvP focus, and a dark/gritty world where reputation is earned, not bought."
  },
  {
    question: "What are the system requirements?",
    answer: "We're still optimizing, but Knight Arena will run on mid-range gaming PCs. We'll release detailed specs closer to launch."
  },
  {
    question: "Can I change my faction after choosing?",
    answer: "Faction choices have consequences in Knight Arena. While we may implement faction changes, they won't be easy or without cost. Choose wisely."
  }
];

export default function Support() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [heroContent, setHeroContent] = useState({
    title: "Support Center",
    subtitle: "Need help? We're here to assist you on your journey"
  });
  const [faqs, setFaqs] = useState(DEFAULT_FAQS);

  useEffect(() => {
    fetch(`${API_URL}/content/support`)
      .then(r => r.json())
      .then(data => {
        const c = data.content || {};
        if (c.hero) {
          setHeroContent(h => ({ ...h, ...c.hero }));
        }
        if (Array.isArray(c.faqs) && c.faqs.length > 0) {
          setFaqs(c.faqs);
        }
      })
      .catch(() => {}); // silent fail, keep defaults
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error("Failed to send");
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending support request:', error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative">
      {/* Background - Fixed position */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/fdec0719c_loading_screen_14.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">
                {heroContent.title}
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {heroContent.subtitle}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {/* Quick Links */}
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 cursor-pointer group">
              <BookOpen className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                Wiki & Guides
              </h3>
              <p className="text-gray-400 mb-6">
                Comprehensive documentation about game mechanics, classes, and systems
              </p>
              <a
                href="https://knightarena.fandom.com/wiki/Knight_Arena_Wiki"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="w-full border-white text-white hover:bg-white/10">
                  Visit Wiki
                </Button>
              </a>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 cursor-pointer group">
              <MessageSquare className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                Discord Support
              </h3>
              <p className="text-gray-400 mb-6">
                Get real-time help from our community and moderators
              </p>
              <Button className="w-full bg-[#5865F2] hover:bg-[#4752C4]">
                Join Discord
              </Button>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 cursor-pointer group">
              <Search className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                FAQ
              </h3>
              <p className="text-gray-400 mb-6">
                Find quick answers to commonly asked questions
              </p>
              <Button variant="outline" className="w-full border-white text-white hover:bg-white/10">
                Browse FAQ
              </Button>
            </Card>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-8 text-white">
              Frequently Asked Questions
            </h2>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-[#8B0000]/20">
                    <AccordionTrigger className="text-left text-white hover:text-white transition-colors">
                      <div className="flex items-center gap-3">
                        <HelpCircle className="w-5 h-5 text-white shrink-0" />
                        {faq.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400 pl-8">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-4xl font-bold mb-8 text-white">
              Contact Support
            </h2>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-gray-400 mb-6">
                    We've received your message and will get back to you as soon as possible.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-[#DAA520]"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-[#DAA520]"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-gray-300">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-[#DAA520]"
                      placeholder="What do you need help with?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-300">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      className="bg-[#0a0a0a] border-[#8B0000]/30 text-white focus:border-[#DAA520] min-h-[150px]"
                      placeholder="Describe your issue or question in detail..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </Card>
          </div>

          {/* Bottom Note */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm">
              Average response time: 24-48 hours • For urgent issues, join our Discord for faster support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
